import { TemplateKey } from "PFApp/constants/templates";
import { buildProfilesOptionsFetcher } from "PFApp/helpers/options/profiles_options";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Modal } from "PFComponents/modal";
import { Select } from "PFComponents/select";
import { ValuesList } from "PFComponents/select/value_box/values_list";
import { Typography } from "PFComponents/typography";
import { useActivityHistoryInvalidate } from "PFCore/hooks/queries/activities/history/use_activity_history_invalidate";
import { useSendInvitation } from "PFCore/hooks/queries/invites/use_send_invitation";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { BasicProfile } from "PFCore/types";
import { Option } from "PFTypes/options";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./share_modal.module.scss";

type ShareModalProps = {
  activityIds: number[];
  templateKey?: TemplateKey;
  invites?: any[];
  onClose?: VoidFunction;
  onSuccess?: (resp?) => void;
};

export const ShareModal = ({ templateKey, activityIds, invites, onClose, onSuccess }: ShareModalProps) => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation(["core", "translation", "activities"]);
  const { showGrowl } = useGrowlStoreActions();

  const invalidateHistory = useActivityHistoryInvalidate(activityIds);

  const [profiles, setProfiles] = useState<BasicProfile[]>([]);

  const { mutate: handleShare, isPending } = useSendInvitation(
    activityIds,
    profiles.map(({ id }) => id),
    {
      onSuccess: (resp) => {
        showGrowl({
          message: templateKey
            ? t("core:activityShare.inviteMessage.activitiesShared", {
                activityType: t(`activities:templates.${templateKey}`)
              })
            : t("core:activityShare.inviteMessage.success")
        });
        onSuccess?.(resp);
        invalidateHistory();
      },
      onError: () =>
        showGrowl({
          kind: "error",
          message: t("core:activityShare.inviteMessage.error")
        })
    }
  );

  const handleClose = () => {
    setProfiles([]);
    onClose?.();
  };

  const handleChange = (options: Option<BasicProfile>[]) =>
    setProfiles(options.map(({ original }) => original));

  return (
    <Modal
      title={
        templateKey
          ? t("core:activityShare.shareActivityTemplates", {
              count: activityIds.length,
              templateKey
            })
          : t("core:activityShare.shareActivities", { count: activityIds.length })
      }
      onClose={handleClose}
      onOK={handleShare}
      disableOKButton={profiles.length === 0 || isPending}
      labelOK={t("core:activityShare.buttonText")}
    >
      <Select<BasicProfile>
        label={t("core:activityShare.buttonLabel")}
        onChange={handleChange}
        value={profiles.map(({ id }) => id)}
        renderDisplayValue={(values) => (
          <ValuesList
            selectedOptions={values.map((value) => ({ ...value, displayElement: null }))}
            onChange={handleChange}
          />
        )}
        fetchOptions={buildProfilesOptionsFetcher()}
        parseOptions={(options: Option<BasicProfile>[]) =>
          options.filter(
            (item) =>
              currentProfile.id !== item.id && !(invites || []).find(({ profileId }) => profileId === item.id)
          )
        }
        onClear={profiles.length > 0 ? () => setProfiles([]) : undefined}
        multiple
        placeholder={t("core:activityShare.buttonPlaceholder")}
        dropdownId="activity-share-modal"
        isFetchingSelectedOptions={false}
      />
      <div className={css.info}>
        <Typography withMargin variant="bodyBold">
          {t("translation:pleaseNote")}
        </Typography>
        <Typography withMargin variant="bodyRegular">
          {t("core:activityShare.buttonInfo", {
            itemsName: templateKey ? t(`activities:templates.${templateKey}_other`) : t("core:activities")
          })}
        </Typography>
      </div>
    </Modal>
  );
};
